import { useState, forwardRef } from 'react';
import {
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  FormErrorMessageProps,
  InputRightElement,
  InputGroup,
  InputGroupProps,
} from '@chakra-ui/react';
import InputMask from 'react-input-mask';
import { ComponentStyleConfig } from '@chakra-ui/theme';
import { PasswordStrength } from 'components/UI/PasswordStrength';
import { FormErrorMessage } from 'components/UI/forms';
import { TogglePassword, ClearButton } from './addons';

export type InputProps = ChakraInputProps & {
  error?: string;
  isPasswordStrength?: boolean;
  isTogglePassword?: boolean;
  isClear?: boolean;
  errorProps?: FormErrorMessageProps;
  containerProps?: InputGroupProps;
  mask?: string;
  rightComponent?: React.ReactElement;
  onClear?(): void;
};

const InputStyles: ComponentStyleConfig = {
  baseStyle: {
    height: 'auto',
  },
  variants: {
    filled: {
      field: {
        borderRadius: 'none',
        border: 'none',
        backgroundColor: 'gray.100',
        _focus: {
          backgroundColor: 'gray.100',
          boxShadow: 'none',
          border: 'none',
        },
      },
    },
  },
  sizes: {
    md: {
      field: {
        px: 3,
        py: 2,
        borderRadius: 3,
      },
    },
  },
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      isPasswordStrength = false,
      isTogglePassword = false,
      isClear = false,
      mask,
      as,
      name,
      errorProps = {},
      error,
      type = 'text',
      size = 'md',
      containerProps,
      variant = 'filled',
      rightComponent,
      onChange,
      onClear,
      ...props
    }: InputProps,
    ref
  ) => {
    const renderPasswordStrength = isPasswordStrength ? <PasswordStrength value={(value as string) ?? ''} /> : null;

    const [showPassword, setShowPassword] = useState(false);

    const handleTogglePassword = () => setShowPassword((prevShow) => !prevShow);

    const renderTogglePassword = isTogglePassword ? (
      <TogglePassword name={name} onClick={handleTogglePassword} visible={showPassword} />
    ) : null;

    const renderClear =
      isClear && typeof value === 'string' && value.length > 0 ? (
        <ClearButton name={name} visible={!!value} onClick={onClear} />
      ) : null;

    const renderRightComponent =
      isTogglePassword || isClear ? (
        <InputRightElement zIndex={1} pr={3} w="auto">
          {renderClear}
          {renderTogglePassword}
          {rightComponent}
        </InputRightElement>
      ) : null;

    return (
      <>
        <InputGroup size={size} {...containerProps}>
          <ChakraInput
            {...props}
            variant={variant}
            as={mask ? InputMask : as}
            mask={mask}
            name={name}
            ref={ref}
            size={size}
            id={name}
            value={value ?? ''}
            onChange={onChange}
            type={showPassword ? 'text' : type}
          />
          {renderRightComponent}
        </InputGroup>
        {renderPasswordStrength}
        <FormErrorMessage {...errorProps}>{error}</FormErrorMessage>
      </>
    );
  }
);

export { Input, InputStyles };
